import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  map,
  of,
  switchMap,
  throwError,
  timer,
} from 'rxjs';
import * as CryptoJS from 'crypto-js';
import {
  IBodyCelphone,
  IBodyCelphoneCode,
} from '../utils/interfaces/celphone.interface';
import { IBodyEmail } from '../utils/interfaces/email.interface';
import { IBodyForgotRut, IBodyRut } from '../utils/interfaces/rut.interface';
import { IBodyPersonalData } from '../utils/interfaces/personal-data.interface';
import { IBodyPasswordLogin } from '../utils/interfaces/login.interface';
import { IBodyForgotPassword } from '../utils/interfaces/forgot-password.interface';
import { IBodyValidator } from '../utils/interfaces/validator.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { IBodyDeviceInit } from '../utils/interfaces/info-computer.interface';
import * as Forge from 'node-forge';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from '@angular/router';
import { RESPONSE_TOKEN } from '../utils/enums/token.enum';
import { IScope } from '../utils/interfaces/scope.interface';
import { RESPONSE_VALIDA } from '../utils/enums/valida.enum';
import { v4 as uuidv4 } from 'uuid';
import { Device } from '@capacitor/device';
import { ISelectAccount } from '../utils/interfaces/select-account.interface';
import { HTTPService } from '@copec-gid/gid-arquitectura.seguridad-autorizacion-modulo';
import { ConfProyecto } from '@copec-gid/gid-arquitectura.utilitarios-base-modulo-a16';
import { RESPONSE_QR_POLLING } from '../utils/enums/qr.enum';
declare global {
  interface Navigator {
    brave?: object;
  }
}
export enum STEP {
  CELPHONE_NUMBER,
  CELPHONE_CODE,
  PASSWORD,
  EMAIL,
  RUT,
  PERSONAL_DATA,
  SUCCESS,
  LOADING,
  TERMS,
  WELCOME,
  FORGOT,
  FORGOT_RUT,
  FORGOT_RUT_CODE,
  NEW_PASSWORD,
  RECOGNIZED,
  SELECT_ACCOUNT,
  OOPS,
  RECOVERY,
  SUCCESSQR,
}
@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private $currentStep = signal<STEP>(STEP.CELPHONE_NUMBER);
  private _walletVerification = false;
  public currentStep = computed(() => this.$currentStep());
  public headers = {};
  public accessToken!: string;
  public secret!: string;
  public tycCopec: { tipo: string; version: number; link: string }[] = [];
  public tycClient!: {
    text: string;
    policy: string;
    terms: string;
    alias: string;
    isPolicyRequired: boolean;
    isTermsRequired: boolean;
    termsVersion: number;
    policyVersion: number;
  };
  public qrData: string = '';
  public isQrScanned!: boolean;
  public isQrAuthenticated!: boolean;
  public isAuthorized = new Subject<RESPONSE_TOKEN>();
  public scopes: IScope[] = [];
  bodyDevice!: IBodyDeviceInit;
  public subscription!: Subscription;
  public selectAccountInfo!: ISelectAccount;
  private route = inject(ActivatedRoute);
  private tokenQueryParam!: string;
  private bridgeTokenQueryParam!: string;
  public readonly hasBridgeToken = new URLSearchParams(window.location.search).has('bridge_token');
  constructor() {
    this.initializeData();
  }

  public initAuthorization(): Observable<any> {
    return HTTPService.instance.callApi(
      { sesion_tipo: 'sesion_crear' },
      `oidcpersonas/webauthorize`,
      { metodo: 'post', tipoAuth: 'user_login' },
    );
  }

  public initDevice(body: IBodyDeviceInit): Observable<any> {
    return HTTPService.instance.callApi(body, `oidcpersonas/tokenget`, {
      metodo: 'post',
      tipoAuth: 'auth',
    });
  }

  public setBridgeToken(body: { bridge_token: string }): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/bridgetokenpost`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers
      )
      .pipe(map((response: any) => response));
  }

  public tokenDelete(body: {}): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        {},
        `oidcpersonas/tokendelete`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public async initializeData(): Promise<void> {
    this.isAuthorized.next(RESPONSE_TOKEN.LOADING);
    try {
      // Los datos del equipo no dependen de webauthorize: se piden en paralelo
      const deviceData = Promise.all([Device.getInfo(), Device.getId()]);
      const respAutorization = await this.initAuthorization().toPromise();
      ConfProyecto.session_key = respAutorization.data.sesion_key;
      ConfProyecto.session_token = respAutorization.data.sesion_token;

      let fechaEpoch: any = new Date(1970, 0, 1, 0, 0, 0);
      let fechaActual: any = new Date();
      let diferencia = fechaActual - fechaEpoch;
      let segundos = Math.floor(diferencia / 1000);
      const random = this.generateRandomString();
      let token: string = '';
      this.route.queryParams.subscribe((params) => {
        this.bridgeTokenQueryParam = params['bridge_token'];
        token = this.bridgeTokenQueryParam ? '' : params['token'];
        this.tokenQueryParam = token;
      });
      // Generar un UUID
      let uniqueId = '';

      if (localStorage.getItem('uniqueId')) {
        uniqueId = localStorage.getItem('uniqueId')!;
      } else {
        uniqueId = uuidv4();
      }
      localStorage.setItem('uniqueId', uniqueId);
      const [info, isVirtual] = await deviceData;
      const bodyDevice: IBodyDeviceInit = {
        idEquipo: uniqueId,
        ...(this.bridgeTokenQueryParam
          ? { bridge_token: this.bridgeTokenQueryParam }
          : token
          ? { token: token }
          : {}),
        pushToken: 'no_push_notification_token',
        versionAplicacion: '0.0.3',
        dataEquipo_SO: info.operatingSystem,
        dataEquipo_SOVersion: info.osVersion,
        dataEquipo_marca: this._getBrowserName(),
        dataEquipo_modelo: info.model,
        dataEquipo_isPhysical: isVirtual.identifier ? 'F' : 'T',
        idCliente: 'WebOidc',
        versionCertificado: 'V1',
        secretA: this.headerLOGIN({
          versionCertificado: 'V1',
          encriptedSecretA: random,
          requestFecha: segundos,
          key: 'c5b5a895298b92380e4110ad8ee81dfbb3bd45c5c50240ff488d2a546a123388',
        }),
      };
      this.bodyDevice = bodyDevice;
      this.isQrScanned = false;
      const respToken = await this.initDevice(bodyDevice).toPromise();

      if (
        typeof respToken?.data === 'object' &&
        respToken.data !== null &&
        'validacion_billetera' in respToken.data &&
        respToken.data.validacion_billetera
      ) {
        this._walletVerification = true;
      }

      switch (respToken.data.codigoRespuesta as RESPONSE_TOKEN) {
        case RESPONSE_TOKEN.EQUIPO_CREADO:
          this.qrData = respToken.data.QR;
          this.accessToken = respToken.data.access_token;
          this.secret = random + respToken.data.secretB;
          localStorage.setItem('secret', JSON.stringify(this.secret));
          localStorage.setItem(
            'redirectUrl',
            JSON.stringify(respToken.data.redirect_url),
          );
          // INITIAL STEP
          if (!this.bridgeTokenQueryParam) {
            this.setStep(STEP.CELPHONE_NUMBER);
            this.isAuthorized.next(respToken.data.codigoRespuesta);
          }
          break;
        case RESPONSE_TOKEN.TOKEN_VIGENTE_CON_VALIDA:
          this.accessToken = respToken.data.access_token;
          const secret: string = JSON.parse(
            localStorage.getItem('secret')!,
          ) as string;
          this.secret = secret;
          localStorage.setItem(
            'redirectUrl',
            JSON.stringify(respToken.data.redirect_url),
          );
          if (!this.bridgeTokenQueryParam) {
            this.checkValida(secret);
          }
          break;
        case RESPONSE_TOKEN.TOKEN_NO_VIGENTE:
          this.reInitApp();
          window.open(respToken.data.redirect_url, '_self');
          break;
        case RESPONSE_TOKEN.TOKEN_NO_EXISTE:
          const redirect_url = JSON.parse(
            localStorage.getItem('redirectUrl')!,
          ) as string;

          if (redirect_url) {
            localStorage.removeItem('redirectUrl');
            window.open(redirect_url, '_self');
          } else {
            this.isAuthorized.next(RESPONSE_TOKEN.TOKEN_NO_EXISTE);
            this.setStep(STEP.OOPS);
          }
          break;
      }
      if (
        (respToken.data.codigoRespuesta === RESPONSE_TOKEN.EQUIPO_CREADO ||
          respToken.data.codigoRespuesta ===
            RESPONSE_TOKEN.TOKEN_VIGENTE_CON_VALIDA) &&
        this.bridgeTokenQueryParam
      ) {
        this.setBridgeToken({ bridge_token: this.bridgeTokenQueryParam })
          .subscribe({
            next: (resp) => {
              if (resp?.data?.redirectUrl) {
                window.open(resp.data.redirectUrl, '_self');
              } else {
                this.isAuthorized.next(RESPONSE_TOKEN.TOKEN_NO_EXISTE);
                this.setStep(STEP.OOPS);
              }
            },
            error: (err) => {
              this.isAuthorized.next(RESPONSE_TOKEN.TOKEN_NO_EXISTE);
              this.setStep(STEP.OOPS);
            },
          });
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.reInitApp();
      } else {
        this.isAuthorized.next(RESPONSE_TOKEN.TOKEN_NO_EXISTE);
        this.setStep(STEP.OOPS);
      }
    }
  }

  public checkValida(hmac: string, fromQR: boolean = false): void {
    this.getValidation().subscribe({
      next: (resp: any) => {
        switch (resp.codigoRespuesta as RESPONSE_VALIDA) {
          case RESPONSE_VALIDA.REDIRECT:
            this.selectAccountInfo = resp;
            this.accessToken = resp.access_token;
            this.secret = hmac;
            if (fromQR) {
              window.open(resp.redirect_url, '_self');
            } else {
              this.setStep(STEP.SELECT_ACCOUNT);
            }
            this.isAuthorized.next(resp.codigoRespuesta);
            break;
          case RESPONSE_VALIDA.VALIDA:
            this.selectAccountInfo = resp;
            this.tycClient = {
              text: resp.texto,
              policy: resp.politica_privacidad_link,
              terms: resp.terminos_y_condiciones_link,
              alias: resp.alias,
              isPolicyRequired: resp.politica_privacidad_obligatorio === 1,
              isTermsRequired: resp.terminos_y_condiciones_obligatorio === 1,
              termsVersion: resp.terminos_y_condiciones_version,
              policyVersion: resp.politica_privacidad_version,
            };
            this.accessToken = resp.access_token;
            this.secret = hmac;
            this.setStep(STEP.TERMS);
            this.scopes = resp.scopes;
            this.isAuthorized.next(3);
            break;
          case RESPONSE_VALIDA.SERVICE_ERROR_REDIRECT:
            window.open(resp.redirect_url, '_self');
            break;
          case RESPONSE_VALIDA.SUCCESS_AUTH_REDIRECT:
            this.accessToken = resp.access_token;
            this.secret = hmac;
            window.open(resp.redirect_url, '_self');
            break;
        }
      },
      complete: () => {
        // this.loadingStep = false;
      },
      error: (err) => {
        // this.loadingStep = false;
      },
    });
  }

  private headerLOGIN(body: any): string {
    const bodyString = JSON.stringify(body);
    const rsa_pub_key = Forge.pki.publicKeyFromPem(environment.publicKeyToken);
    const encryptedRSA = rsa_pub_key.encrypt(bodyString, 'RSA-OAEP', {
      md: Forge.md.sha256.create(),
      mgf1: Forge.mgf.mgf1.create(Forge.md.sha256),
    });
    const hexString = Forge.util.bytesToHex(encryptedRSA);
    return hexString;
  }

  private generateRandomString(): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;

    for (let i = 0; i < 20; i++) {
      const randomIndex = Math.floor(Math.random() * charactersLength);
      result += characters.charAt(randomIndex);
    }

    return result;
  }

  private reInitApp(): void {
    this.initializeData();
  }

  public setStep(step: STEP): any {
    this.$currentStep.set(step);
  }

  public setCelphone(body: IBodyCelphone): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/telefono`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setCelphoneCode(body: IBodyCelphoneCode): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/smstelefono`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setEmail(body: IBodyEmail): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/mail`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setValidation(body: IBodyValidator): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/valida`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public getValidation(): Observable<any> {
    return HTTPService.instance
      .callApi({}, `oidcpersonas/valida?token=${this.tokenQueryParam}`, {
        metodo: 'get',
        tipoAuth: 'auth',
      })
      .pipe(map((response: any) => response.data));
  }

  public setRut(body: IBodyRut): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/rut`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setForgotPassword(body: IBodyForgotPassword): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/contrasena`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public startQRPolling(): void {
    this.subscription = timer(0, 3000) // Emite cada 1 segundo
      .pipe(switchMap(() => this._checkQrPolling()))
      .subscribe(
        (response) => {
          switch (response.codigoRespuesta as RESPONSE_QR_POLLING) {
            case RESPONSE_QR_POLLING.VALIDO:
              break;
            case RESPONSE_QR_POLLING.AUTENTICADO:
              this.isQrScanned = false;
              this.isQrAuthenticated = true;
              this.setStep(STEP.SUCCESSQR);
              setTimeout(() => {
                this.checkValida(this.secret, true);
              }, 2000);
              break;
            case RESPONSE_QR_POLLING.EXPIRADO:
              this.isQrScanned = false;
              this.qrData = response.newQR;
              break;
            case RESPONSE_QR_POLLING.ESCANEADO:
              this.isQrScanned = true;
              break;

            default:
              break;
          }
        },
        (error) => {
          if (error.status === 403) {
            this.reInitApp();
          }
          this.subscription.unsubscribe();
        },
      );
  }

  public setForgotRut(body: IBodyForgotRut): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/tokentelefono`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setPersonalData(body: IBodyPersonalData): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/usuario`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setLogin(body: IBodyPasswordLogin): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/token`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public setForgot(body: {}): Observable<any> {
    let headers = {
      firma: this.generateHmac(body),
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        body,
        `oidcpersonas/tokenmail`,
        { metodo: 'post', tipoAuth: 'auth' },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  public generateHmac(body: any): string {
    const hmac = CryptoJS.HmacSHA256(this.stringifyJson(body), this.secret);
    const mac = hmac.toString(CryptoJS.enc.Hex);
    return mac;
  }

  private stringifyJson(jsonObject: any): string {
    return JSON.stringify(jsonObject);
  }

  public _checkQrPolling(): Observable<any> {
    let headers = {
      access_token: this.accessToken,
    };
    return HTTPService.instance
      .callApi(
        {},
        `oidcpersonas/sesionqr`,
        {
          metodo: 'get',
          tipoAuth: 'auth',
        },
        headers,
      )
      .pipe(map((response: any) => response.data));
  }

  getWalletVerification(): boolean {
    const value = this._walletVerification;
    this._walletVerification = false;
    return value;
  }

  private _getBrowserName(): string {
    const userAgent = window.navigator.userAgent;
    // Detectar Brave verificando si `navigator.brave` es un objeto
    if (typeof navigator.brave === 'object') {
      return 'Brave';
    } else if (/edg/i.test(userAgent)) {
      return 'Edge';
    } else if (
      /chrome|crios|crmo/i.test(userAgent) &&
      !/edge|opr\//i.test(userAgent)
    ) {
      return 'Chrome';
    } else if (/firefox|fxios/i.test(userAgent)) {
      return 'Firefox';
    } else if (
      /safari/i.test(userAgent) &&
      !/chrome|crios|crmo/i.test(userAgent)
    ) {
      return 'Safari';
    } else if (/msie|trident/i.test(userAgent)) {
      return 'Internet Explorer';
    } else if (/opr|opera/i.test(userAgent)) {
      return 'Opera';
    } else {
      return 'Unknown';
    }
  }
}
